import { colors } from '@/styles';
import { CircularProgress, Grid, MenuItem, TextField } from '@material-ui/core';
import React, { useCallback, useContext, useState } from 'react';
import { compose } from 'recompose';
import { Context, withContext } from './store';
import { css } from '@emotion/react';
import { OnwardTab, OnwardTabContainer } from '../Tabs';
import { PrimaryButton } from '@/styles/blocks';
import { useClientUser } from '@/hooks';
import { PrimaryHeader } from '@/styles/typography';
import SearchInput from '../misc/SearchInput';
import { TABS } from './constants';
import { manifestHref } from './utils';
import CustomizableTable from '../CustomizableTable';
import CreateManifest from './modals/CreateManifest';
import ChoiceModal from './modals/ChoiceModal';
import Snackbar from '../Snackbar';
import { useNavigate } from 'react-router';
import { MANIFEST_STATUS_LABELS, MANIFEST_TYPE_LABELS } from '@/constants/manifestStatuses';
import { Body1 } from '../DispatchPlan/blocks';

const Manifests = () => {
    const { state, callbacks, loading } = useContext(Context);
    const { user_id } = useClientUser();
    const navigate = useNavigate();
    const [choosingDirection, setChoosingDirection] = useState(false);
    // Column order/widths per tab, kept here so they survive the table remounting while a query reloads.
    const [tableLayouts, setTableLayouts] = useState({});

    const tabValue = state.tab.value;
    const handleLayoutChange = useCallback(
        (layout) => setTableLayouts((prev) => ({ ...prev, [tabValue]: layout })),
        [tabValue]
    );
    const savedLayout = tableLayouts[tabValue];

    const openCreateForm = (direction) => {
        setChoosingDirection(false);
        callbacks.openCreateManifest(direction);
    };

    return (
        <Grid
            container
            direction="column"
            css={css`
                height: 100%;
                flex-wrap: nowrap;
            `}
        >
            <Snackbar
                open={!!state.notification.message}
                {...state.notification}
                handleClose={callbacks.clearNotification}
            />
            <CreateManifest />
            <ChoiceModal
                open={choosingDirection}
                title="Order Manifest Type"
                subtitle="What type of order manifest are you building?"
                onClose={() => setChoosingDirection(false)}
                options={[
                    {
                        label: 'Inbound',
                        description: 'Record items arriving at the warehouse for receiving or crossdocks.',
                        onSelect: () => openCreateForm('INBOUND'),
                    },
                    {
                        label: 'Outbound',
                        description:
                            'Record items leaving the warehouse for final mile deliveries, OS&Ds, or will calls.',
                        onSelect: () => openCreateForm('OUTBOUND'),
                    },
                ]}
            />
            <Grid
                container
                direction="row"
                css={css`
                    padding: 0rem 1rem;
                    border-bottom: 1px solid ${colors.greys.border};
                    background: ${colors.white.primary};
                    align-items: center;
                    justify-content: space-between;
                    padding-top: 1.5rem;
                    padding-bottom: 1.5rem;
                `}
            >
                <Grid container xs={3} alignItems="center">
                    <PrimaryHeader
                        css={css`
                            margin: 0;
                        `}
                    >
                        Manifests
                    </PrimaryHeader>
                </Grid>
                <Grid container xs={3} justifyContent="flex-end">
                    {state.tab.value !== 'EXTERNAL' &&
                        state.tab.value !== 'EXTERNALOUT' &&
                        state.tab.value !== 'REQUESTS' && (
                            <PrimaryButton onClick={() => setChoosingDirection(true)}>Create Manifest</PrimaryButton>
                        )}
                </Grid>
            </Grid>
            {state.tab.value === 'REQUESTS' && (
                <Grid
                    container
                    direction="row"
                    css={css`
                        padding: 0rem 0rem 1rem 2rem;
                        align-items: center;
                        justify-content: space-between;
                        width: auto;
                    `}
                >
                    <Grid container xs={6}>
                        <Body1>
                            These are will-call inventory requests that have been sent to you by a partnered shipper.
                            Click a manifest for more details and to assign inventory.
                        </Body1>
                    </Grid>
                </Grid>
            )}
            <Grid
                container
                direction="row"
                css={css`
                    padding: 1rem;
                    margin: 0rem 1rem 2rem 2rem;
                    align-items: center;
                    justify-content: space-between;
                    gap: 1rem;
                    background-color: white;
                    width: auto;
                `}
            >
                <Grid
                    item
                    css={css`
                        flex: 2 1 0;
                    `}
                >
                    <SearchInput
                        css={css`
                            width: 100%;
                        `}
                        placeholder="Search manifest, order, PO, SKU, barcode, or serial"
                        onChange={(search) => callbacks.setSearchManifests(search)}
                        initialValue={state.searchManifests}
                        size="medium"
                    />
                </Grid>
                <Grid
                    item
                    css={css`
                        flex: 1 1 0;
                    `}
                >
                    <TextField
                        select
                        variant="outlined"
                        label="Shipper"
                        value={state.filters.shipperSelect || ''}
                        onChange={(e) => {
                            const val = e.target.value !== '' ? e.target.value : null;
                            callbacks.setFilters((prev) => ({ ...prev, shipperSelect: val }));
                        }}
                        css={css`
                            width: 100%;
                            background-color: white;
                        `}
                    >
                        <MenuItem key={''} value={''}>
                            Select...
                        </MenuItem>
                        {state.shipperOptions.map((option) => (
                            <MenuItem key={option} value={option}>
                                {option}
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>
                <Grid
                    item
                    css={css`
                        flex: 1 1 0;
                    `}
                >
                    <TextField
                        select
                        variant="outlined"
                        label="Status"
                        value={state.filters.status || 'ALL'}
                        onChange={(e) => {
                            const val = e.target.value !== 'ALL' ? e.target.value : null;
                            callbacks.setFilters((prev) => ({ ...prev, status: val }));
                        }}
                        css={css`
                            width: 100%;
                            background-color: white;
                        `}
                        SelectProps={{
                            renderValue: (value) => {
                                // For Active status, show a cleaner representation
                                if (value === 'ACTIVE') {
                                    return 'Active';
                                }
                                // For other statuses, show the label
                                return value === 'ALL' ? 'All' : MANIFEST_STATUS_LABELS[value]?.label || value;
                            },
                        }}
                    >
                        <MenuItem key={'ALL'} value={'ALL'}>
                            All
                        </MenuItem>
                        <MenuItem key={'ACTIVE'} value={'ACTIVE'}>
                            <span
                                css={css`
                                    display: flex;
                                    align-items: center;
                                    font-weight: 500;
                                `}
                            >
                                <span
                                    css={css`
                                        margin-right: 8px;
                                        font-size: 10px;
                                        color: ${colors.greys.secondary};
                                    `}
                                >
                                    ▼
                                </span>
                                Active
                            </span>
                        </MenuItem>
                        {state.tab.activeStatuses?.map((option) => (
                            <MenuItem
                                key={option}
                                value={option}
                                css={css`
                                    padding-left: 32px;
                                    font-size: 0.9em;
                                    color: ${colors.greys.secondary};
                                `}
                            >
                                {MANIFEST_STATUS_LABELS[option]?.label || option}
                            </MenuItem>
                        ))}
                        <MenuItem
                            disabled
                            css={css`
                                padding: 0;
                                margin: 4px 0;
                                border-bottom: 1px solid ${colors.greys.border};
                            `}
                        />
                        {state.tab.statusOptions
                            .filter((option) => !state.tab.activeStatuses?.includes(option))
                            .map((option) => (
                                <MenuItem key={option} value={option}>
                                    {MANIFEST_STATUS_LABELS[option]?.label || option}
                                </MenuItem>
                            ))}
                    </TextField>
                </Grid>
                <Grid
                    item
                    css={css`
                        flex: 1 1 0;
                    `}
                >
                    <TextField
                        select
                        variant="outlined"
                        label="Type"
                        value={state.filters.type || 'ALL'}
                        onChange={(e) => {
                            const val = e.target.value !== 'ALL' ? e.target.value : null;
                            callbacks.setFilters((prev) => ({ ...prev, type: val }));
                        }}
                        css={css`
                            width: 100%;
                            background-color: white;
                        `}
                    >
                        <MenuItem key={'ALL'} value={'ALL'}>
                            All
                        </MenuItem>
                        {state.tab.typeOptions.map((option) => (
                            <MenuItem key={option} value={option}>
                                {MANIFEST_TYPE_LABELS[option] || option}
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>
            </Grid>
            <Grid
                container
                direction="row"
                css={css`
                    margin: 0rem 1rem 0rem 2rem;
                    background-color: white;
                    border-bottom: 1px solid ${colors.greys.border};
                    width: auto;
                `}
            >
                <OnwardTabContainer
                    value={state.tab.value}
                    onChange={(_, tab) => {
                        callbacks.setTab(TABS[tab]);
                        callbacks.setFilters(TABS[tab]?.defaultFilters || {});
                    }}
                >
                    {Object.values(TABS)
                        .filter(
                            (tab) =>
                                ((tab.value !== 'EXTERNAL' && tab.value !== 'EXTERNALOUT') || state.hasExternal) &&
                                (tab.value !== 'REQUESTS' || (state.draftManifests && state.draftManifests.length > 0))
                        )
                        .map((tab) => (
                            <OnwardTab
                                key={`manifest-tab-${tab.value}`}
                                label={
                                    tab.value === 'REQUESTS' ? (
                                        <div
                                            css={css`
                                                display: flex;
                                                align-items: center;
                                            `}
                                        >
                                            {tab.label}
                                            <span
                                                css={css`
                                                    width: 8px;
                                                    height: 8px;
                                                    background-color: #dc3545;
                                                    border-radius: 50%;
                                                    display: inline-block;
                                                    margin-left: 6px;
                                                `}
                                            />
                                        </div>
                                    ) : (
                                        tab.label
                                    )
                                }
                                value={tab.value}
                            />
                        ))}
                </OnwardTabContainer>
            </Grid>
            <Grid
                container
                css={css`
                    padding: 0rem 1rem 2rem 2rem;
                    flex-grow: 1;
                    flex-direction: column;
                    flex-wrap: nowrap;
                    min-height: 0;
                    align-items: center;
                `}
            >
                {loading.queryLoading ? (
                    <Grid
                        item
                        css={css`
                            margin-top: 2rem;
                        `}
                    >
                        <CircularProgress color="primary" size="200px" />
                    </Grid>
                ) : (
                    <Grid
                        item
                        css={css`
                            flex: 1 1 0;
                            min-height: 0;
                            width: 100%;
                            display: flex;
                            flex-direction: column;
                            border: 1px solid ${colors.greys.border};
                            border-radius: 5px;
                            overflow: hidden;
                        `}
                    >
                        <CustomizableTable
                            key={tabValue}
                            data={
                                tabValue === 'EXTERNAL' || tabValue === 'EXTERNALOUT'
                                    ? state.externalManifests
                                    : tabValue === 'REQUESTS'
                                    ? state.draftManifests
                                    : state.manifests
                            }
                            columns={state.tab.columns}
                            callbacks={{ onAssignError: callbacks.onAssignError }}
                            getRowId={(manifest) => manifest.manifest_id}
                            manualSortBy={false}
                            virtualizeRows={true}
                            initialState={
                                savedLayout && {
                                    hiddenColumns: savedLayout.hiddenColumns,
                                    columnOrder: savedLayout.columnOrder,
                                    columnResizing: { columnWidths: savedLayout.columnWidths },
                                }
                            }
                            onLayoutChange={handleLayoutChange}
                            onRowClick={(manifest, e) => {
                                if (manifest.isDraft) {
                                    // Draft requests skip the choice dialogs and open the
                                    // create form directly, prefilled as a will-call outbound.
                                    callbacks.openDraftManifest(manifest);
                                    return;
                                }
                                const href = manifestHref(manifest.manifest_id, state.searchManifests);
                                e.preventDefault();
                                if (e.ctrlKey || e.metaKey) {
                                    window.open(href, '_blank');
                                } else {
                                    navigate(href);
                                }
                            }}
                        />
                    </Grid>
                )}
            </Grid>
        </Grid>
    );
};

export default compose(withContext)(Manifests);
